import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "@/lib/constants";

const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/calculator", label: "Calculator" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" }
];

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-white/85 backdrop-blur-xl border-b border-border shadow-sm" : "bg-white/70 backdrop-blur-md"
            }`}
        >
            <div className="container-prose flex items-center justify-between h-20">
                <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
                    <div className="w-9 h-9 rounded-sm bg-[#1A362B] text-white grid place-items-center font-serif text-xl group-hover:bg-[#12261E] transition">FW</div>
                    <div className="leading-tight">
                        <div className="font-serif text-lg text-[#1A362B] font-medium tracking-tight">{BUSINESS.brand}</div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B7280]">Australia-wide</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm font-medium transition-colors ${
                                    isActive ? "text-[#1A362B]" : "text-[#1C1C1C]/70 hover:text-[#1A362B]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={`tel:${BUSINESS.phoneTel}`}
                        data-testid="header-call-button"
                        className="hidden xl:inline-flex items-center gap-2 text-sm font-medium text-[#1A362B] hover:text-[#C69C6D] transition"
                    >
                        <Phone className="w-4 h-4" />
                        {BUSINESS.phone}
                    </a>
                    <Link to="/contact" data-testid="header-cta-button" className="btn-primary">
                        Book Consultation
                    </Link>
                </div>

                <button
                    onClick={() => setOpen((o) => !o)}
                    data-testid="mobile-menu-toggle"
                    className="lg:hidden p-2 text-[#1A362B]"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu">
                    <div className="container-prose py-6 flex flex-col gap-4">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-base font-medium py-2 ${isActive ? "text-[#1A362B]" : "text-[#1C1C1C]/80"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <a href={`tel:${BUSINESS.phoneTel}`} data-testid="mobile-call-link" className="btn-outline mt-2">
                            <Phone className="w-4 h-4" /> {BUSINESS.phone}
                        </a>
                        <Link to="/contact" data-testid="mobile-cta-button" className="btn-primary">Book Consultation</Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
